import React, { Suspense, useContext, useEffect, useMemo } from 'react';
import { Box, BoxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LayoutContext } from './LayoutContext';
import AppLoader from '../AppLoader';
import LayoutAppBar, { LayoutAppBarProps } from './LayoutAppBar';
import OrganizationsStore from '../../stores/observables/OrganizationsStore';
import PoweredBy from './PoweredBy';
import PrivacyPolicy from './PrivacyPolicy';
import useAppMode from '../../lib/hook/config/useAppMode';
import { isMobileWebView } from '../../utils/helpers';
import { isiOS } from '../../lib/Device';

const DEFAULT_BACKGROUND = 'var(--background-color)';

export interface LayoutProps {
  background?: string;
  overflow?: boolean;
  showPoweredBy?: boolean;
  showPrivacyPolicy?: boolean;
  sx?: BoxProps['sx'];
}

interface Props extends LayoutProps {
  appBar?: React.ReactNode;
  children: React.ReactNode | ((footer: React.ReactNode) => React.ReactNode);
  disableAppBarRegistration?: boolean;
  LayoutAppBarProps?: LayoutAppBarProps;
  LoaderComponent?: React.ComponentType;
  main?: boolean;
}

const Layout: React.FC<Props> = (props: Props): JSX.Element => {
  const { hideAppBar, registerAppBar } = useContext(LayoutContext);

  const theme = useTheme();
  const { isEmbeddedMode } = useAppMode({ strict: true });

  const {
    appBar,
    background,
    children,
    disableAppBarRegistration = false,
    LayoutAppBarProps,
    LoaderComponent,
    main = false,
    overflow = false,
    showPoweredBy = false,
    showPrivacyPolicy = false,
    sx,
  } = props;

  const hasAppBar = useMemo(
    () => !!appBar && !(main && hideAppBar),
    [appBar, hideAppBar, main]
  );

  /**
   * The layout's height follows these rules:
   *  - If the layout is in embedded mode, the height is calculated based on the
   *    available height of the parent container, taking into account the height
   *    of its app bar.
   *  - If the layout is the main layout, the height is set to 100vh.
   *  - Otherwise, the height is set to 100%.
   */
  const height = useMemo(
    () => {
      let themeSpacing = 0;

      if (isEmbeddedMode) {
        if (isMobileWebView()) {
          if (isiOS()) {
            themeSpacing = 13;
          } else themeSpacing = 2;
        } else {
          themeSpacing = 8;
        }
      }

      const adjustment = theme.spacing(themeSpacing);
      return main ? `calc(100vh - ${adjustment})` : '100%';
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEmbeddedMode, main]
  );

  useEffect(() => {
    if (!appBar || disableAppBarRegistration) {
      return;
    }

    const unregister = registerAppBar();
    return () => {
      unregister();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableAppBarRegistration]);

  const renderAppBar = () =>
    !hasAppBar ? undefined : (
      <LayoutAppBar {...LayoutAppBarProps}>{appBar}</LayoutAppBar>
    );

  /**
   * Render powered by only on View, Review and Preview DSR / Templates.
   * Otherwise render a box with the same height so it doesn't affect other UI elements
   * such as Intercom button
   */
  const renderPoweredBy = (): JSX.Element => {
    if (showPoweredBy) {
      return <PoweredBy />;
    }

    return (
      <Box
        sx={(theme) => ({
          marginTop: theme.spacing(2),
        })}
      />
    );
  };

  const renderPrivacyPolicy = (): JSX.Element | undefined =>
    !showPrivacyPolicy ? undefined : <PrivacyPolicy />;

  const renderFooter = (): JSX.Element | undefined => {
    const currentOrganization = OrganizationsStore.getCurrentOrganization();
    if (!currentOrganization) {
      return;
    }

    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        pt={2}
      >
        {renderPoweredBy()}
        {renderPrivacyPolicy()}
      </Box>
    );
  };

  const Loader = LoaderComponent || AppLoader;

  return (
    <Box
      className="AppLayout"
      sx={{
        background: main ? DEFAULT_BACKGROUND : background,
        display: 'flex',
        flexDirection: 'column',
        height,
        justifyContent: 'space-between',
        overflow: overflow ? undefined : 'hidden',
        position: isEmbeddedMode ? 'relative' : undefined,
        width: '100%',
        zIndex: isEmbeddedMode ? 0 : undefined,
        ...sx,
      }}
    >
      {renderAppBar()}
      <Suspense fallback={<Loader />}>
        {typeof children === 'function' ? children(renderFooter()) : children}
      </Suspense>
    </Box>
  );
};

export default Layout;
